import ReactDOM from 'react-dom';
import { CustomSchema } from '../diagram/CustomSchema';
import UncontrolledDiagram from '../diagram/UncontrolledDiagram';

const IDE_ID = 'ide';

/**
 * An interface for a bundle of schema operations.
 */
interface SchemaMethodBundle {
    deleteNodeFromSchema: Function;
    onChange: Function;
    addNode: Function;
}

/**
 * Manually renderes diagram component using ReactDOM. This is a singleton class;
 * use getIDE method to get the instance of the ide.
 */
export default class DiagramIDE {
    ideId: string;
    validatedSchemas: Map<string, CustomSchema>;
    schemaMethods: Map<string, SchemaMethodBundle>;

    displaySchemaId: string;

    private static globalNextId = 0;

    public static getID(): string {
        return String(DiagramIDE.globalNextId++);
    }


    private static instance: DiagramIDE;


    /**
     * Private constructor
     * @param ideId the ID of the DOM element where the IDE diagram should be rendered
     */
    private constructor(ideId: string) {
        this.validatedSchemas = new Map();
        this.schemaMethods = new Map();
        this.displaySchemaId = 'main';
        this.ideId = ideId;
    }

    public static getIDE() {
        if (!DiagramIDE.instance) {
            DiagramIDE.instance = new DiagramIDE(IDE_ID);
        }

        return DiagramIDE.instance;
    }

    /**
     * Renders diagram RFC in the DOM element with the ideId as the id.
     * This method is only called when a fresh diagram is needed; it is
     * not called when an existing diagram needs updating.
     */
    public renderDiagram() {
        console.log('rendering IDE');

        let toRender = (<div>
            <UncontrolledDiagram schemaId={this.displaySchemaId} />
        </div>);

        ReactDOM.render(toRender, document.getElementById(this.ideId));
    }

    /**
     * Unmounts the current diagram and calls renderDiagram.
     */
    public redrawDiagram() {
        let leRoot = document.getElementById(this.ideId);
        if (leRoot) {
            ReactDOM.unmountComponentAtNode(leRoot);
        }
        this.renderDiagram();
    }

    /**
     * Add a VALIDATED schema to the diagram before a diagram is unmounted.
     * @param schemaId id of schema being added
     * @param schema schema being added
     * @param methods a bundle of schema methods
     */
    public addSchema(schemaId: string, schema: CustomSchema, methods: SchemaMethodBundle): void {
        this.validatedSchemas.set(schemaId, schema);
        this.schemaMethods.set(schemaId, methods)
    }

    /**
     * Setter for displaySchemaId. Should be used to change to an EXISTING
     * schema. Calls redrawDiagram to update DOM.
     * @param schemaId id of new schema being displayed.
     */
    public setDisplaySchemaId(schemaId: string): void {
        this.displaySchemaId = schemaId;
        this.redrawDiagram();
    }

    /**
     * Loads validated schemas from save; calls to rerender.
     * @param schemas validated schemas from the save
     * @param startID the new value for the node id counter
     */
    public loadSchemas(schemas: Map<string, CustomSchema>, startID: string): void {
        this.validatedSchemas = schemas;
        DiagramIDE.globalNextId = Number(startID);
        this.schemaMethods = new Map(); // old methods no longer valid

        this.displaySchemaId = 'main';
        this.redrawDiagram();
    }

    /**
     * Searches through schema to find nodes with nodeId and flag them as problematic.
     * This is done when a node throws a TeaBlocksError.
     * 
     * @param nodeId the id's of nodes that are to be flagged as problematic.
     */
    public flagNodes(...nodeId: string[]): void {
        nodeId.forEach(id => {
            for (let schema of Array.from(this.validatedSchemas.values())) {
                let foundNode = schema.nodes.find(node => node.id === id);
                if (foundNode) {
                    foundNode.data.problematic = true;
                    console.log(`Flagged ${id}!`);
                    break;
                }
            }
        })
    }

    /**
     * Goes through each node in each schema and sets the problematic flag to
     * false.
     */
    public unflagNodes(): void {
        this.validatedSchemas.forEach(schema => {
            schema.nodes.forEach(node => node.data.problematic = false);
        })
    }
}
